// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/** A caller-owned content identity, independent of the conversion's bundle grouping. */
export interface ArtifactPage {
  key: string;
  logicalPage: number;
  sourcePage: number;
}

/** One immutable generated PDF and its one-based page mappings. */
export interface ArtifactBundle {
  blob: Blob;
  pageCount: number;
  pages: readonly ArtifactPage[];
}

/** The Blob remains usable after eviction, clearing, or closing this cache. */
export interface CachedArtifactPage {
  id: string;
  blob: Blob;
  pageCount: number;
  sourcePage: number;
}

const DATABASE = "unilaiva-multikey-artifacts";
const DEFAULT_BYTES = 128 * 1024 * 1024;
const MAX_METADATA_BYTES = 1024 * 1024;
const MAX_BINARY_BYTES = 32 * 1024 * 1024;
const STORES = ["bundles", "pages", "metadata"];
const PAYLOADS = "payloads";
const encoder = new TextEncoder();

interface BundleRecord {
  id: string;
  blob?: Blob;
  byteLength?: number;
  pageCount: number;
  checksum: number;
  lastUsed: number;
}
interface PageRecord extends ArtifactPage {
  id: string;
  checksum: number;
}
interface MetadataRecord {
  key: string;
  json: string;
  checksum: number;
  lastUsed: number;
}
interface Records {
  bundles: Map<string, BundleRecord>;
  pages: Map<string, PageRecord>;
  metadata: Map<string, MetadataRecord>;
}

function validKey(key: unknown): key is string {
  return typeof key === "string" && key.length > 0 && key.length <= 8192;
}
function positive(value: number): boolean {
  return Number.isSafeInteger(value) && value > 0;
}

function storedBytes(row: BundleRecord): number {
  if (row.blob instanceof Blob) return row.blob.size;
  return row.blob === undefined && positive(row.byteLength!) && row.byteLength! <= MAX_BINARY_BYTES
    ? row.byteLength!
    : 0;
}

function hashBytes(bytes: Uint8Array, seed = 2166136261): number {
  for (const byte of bytes) seed = Math.imul(seed ^ byte, 16777619) >>> 0;
  return seed;
}

function pageChecksum(page: ArtifactPage, id: string): number {
  return hashBytes(
    encoder.encode(JSON.stringify([page.key, id, page.logicalPage, page.sourcePage])),
  );
}

/** Bounded reads, including every byte. This detects damage, not malicious substitutions. */
async function checksum(blob: Blob, pageCount: number): Promise<number> {
  const header = await blob.slice(0, 5).text();
  const tail = await blob.slice(Math.max(0, blob.size - 1024)).text();
  if (header !== "%PDF-" || !/%%EOF\s*$/.test(tail)) throw new Error("Invalid PDF framing");
  let hash = hashBytes(encoder.encode(`${pageCount}:`));
  for (let offset = 0; offset < blob.size; offset += 256 * 1024) {
    const bytes = new Uint8Array(await blob.slice(offset, offset + 256 * 1024).arrayBuffer());
    hash = hashBytes(bytes, hash);
  }
  return hash;
}

/**
 * Parent-only, best-effort derivative storage. Keys must already include validated edition,
 * geometry, complete page selections and processor policy; this module cannot infer them.
 * The budget charges shared PDF bytes once, UTF-8 metadata/index bytes and record overhead.
 * Browser-managed database allocation overhead is not observable. Metadata is bounded JSON;
 * callers still own its semantic/source-identity validation on retrieval.
 */
export class MultikeyArtifactCache {
  private readonly factory: IDBFactory | undefined;
  private readonly maxBytes: number;
  private readonly databaseName: string;
  private opening: Promise<IDBDatabase | undefined> | undefined;
  private database: IDBDatabase | undefined;
  private closed = false;
  private generation = 0;
  private binaryStorage = false;

  /** Open lazily, with a default 128 MiB budget; an explicit zero disables retention. */
  constructor(options?: { indexedDB?: IDBFactory; maxBytes?: number; databaseName?: string }) {
    // Accessing the global itself can throw in restricted browser contexts.
    try {
      this.factory = options?.indexedDB ?? globalThis.indexedDB;
    } catch {
      this.factory = undefined;
    }
    const limit = options?.maxBytes ?? DEFAULT_BYTES;
    this.maxBytes = Number.isSafeInteger(limit) && limit >= 0 ? limit : DEFAULT_BYTES;
    this.databaseName = options?.databaseName ?? DATABASE;
  }

  private open(): Promise<IDBDatabase | undefined> {
    if (this.closed || !this.factory) return Promise.resolve(undefined);
    if (this.opening) return this.opening;
    this.opening = new Promise(resolve => {
      let settled = false;
      const finish = (db?: IDBDatabase) => {
        if (settled || this.closed) {
          db?.close();
          if (!settled) resolve(undefined);
        } else {
          settled = true;
          this.database = db;
          resolve(db);
        }
      };
      try {
        const request = this.factory!.open(this.databaseName, 2);
        request.onupgradeneeded = () => {
          for (const name of STORES) {
            if (!request.result.objectStoreNames.contains(name))
              request.result.createObjectStore(name, {
                keyPath: name === "bundles" ? "id" : "key",
              });
          }
          request.result.createObjectStore(PAYLOADS, { keyPath: "id" });
        };
        request.onsuccess = () => {
          const db = request.result;
          db.onversionchange = () => {
            db.close();
            this.database = undefined;
            this.opening = undefined;
          };
          finish(db);
        };
        request.onerror = () => finish();
        request.onblocked = () => finish();
      } catch {
        finish();
      }
    });
    return this.opening;
  }

  /** One serialized transaction owns publication, shared-bundle cleanup and LRU accounting. */
  private async update<T>(
    action: (records: Records, tick: number, tx: IDBTransaction) => T,
  ): Promise<T | undefined> {
    const db = await this.open();
    if (!db || this.closed) return undefined;
    return new Promise(resolve => {
      let result: T | undefined;
      try {
        const tx = db.transaction([...STORES, PAYLOADS], "readwrite");
        tx.oncomplete = () => resolve(result);
        tx.onabort = () => resolve(undefined);
        const requests = STORES.map(name => tx.objectStore(name).getAll());
        const payloadKeys = tx.objectStore(PAYLOADS).getAllKeys();
        let pending = requests.length + 1;
        for (const request of [...requests, payloadKeys]) {
          request.onsuccess = () => {
            if (--pending) return;
            try {
              const original: Records = {
                bundles: new Map(requests[0].result.map((row: BundleRecord) => [row.id, row])),
                pages: new Map(requests[1].result.map((row: PageRecord) => [row.key, row])),
                metadata: new Map(requests[2].result.map((row: MetadataRecord) => [row.key, row])),
              };
              const records: Records = {
                bundles: new Map(original.bundles),
                pages: new Map(original.pages),
                metadata: new Map(original.metadata),
              };
              this.prune(records);
              let tick = Date.now();
              for (const row of records.bundles.values()) tick = Math.max(tick, row.lastUsed + 1);
              for (const row of records.metadata.values()) tick = Math.max(tick, row.lastUsed + 1);
              result = action(records, tick, tx);
              this.prune(records);
              for (const name of STORES as (keyof Records)[]) {
                const before = original[name] as Map<string, unknown>;
                const after = records[name] as Map<string, unknown>;
                const store = tx.objectStore(name);
                for (const key of before.keys()) if (!after.has(key)) store.delete(key);
                for (const [key, row] of after) if (row !== before.get(key)) store.put(row);
              }
              for (const id of payloadKeys.result)
                if (typeof id !== "string" || !records.bundles.has(id))
                  tx.objectStore(PAYLOADS).delete(id);
            } catch {
              try {
                tx.abort();
              } catch {
                // An external storage failure may already have aborted this transaction.
              }
            }
          };
        }
      } catch {
        resolve(undefined);
      }
    });
  }

  private prune(records: Records): void {
    for (const [id, row] of records.bundles) {
      if (
        !validKey(id) ||
        !positive(storedBytes(row)) ||
        storedBytes(row) > this.maxBytes ||
        !positive(row.pageCount) ||
        row.pageCount > 4096 ||
        !Number.isInteger(row.checksum) ||
        row.checksum < 0 ||
        row.checksum > 0xffffffff ||
        !Number.isSafeInteger(row.lastUsed) ||
        row.lastUsed < 0
      )
        records.bundles.delete(id);
    }
    const costs = new Map<string, number>();
    for (const [key, row] of records.pages) {
      const bundle = records.bundles.get(row.id);
      if (
        !validKey(key) ||
        !bundle ||
        !positive(row.logicalPage) ||
        !positive(row.sourcePage) ||
        row.sourcePage > bundle.pageCount ||
        row.checksum !== pageChecksum(row, row.id)
      ) {
        records.pages.delete(key);
      } else {
        costs.set(row.id, (costs.get(row.id) ?? 0) + encoder.encode(key).length + 128);
      }
    }
    const entries: { key: string; kind: "bundles" | "metadata"; bytes: number; age: number }[] = [];
    for (const [id, row] of records.bundles) {
      if (!costs.has(id)) records.bundles.delete(id);
      else
        entries.push({
          key: id,
          kind: "bundles",
          bytes: storedBytes(row) + costs.get(id)! + 128 + (row.blob ? 0 : 128),
          age: row.lastUsed,
        });
    }
    for (const [key, row] of records.metadata) {
      if (
        !validKey(key) ||
        typeof row.json !== "string" ||
        !Number.isSafeInteger(row.lastUsed) ||
        row.lastUsed < 0 ||
        encoder.encode(row.json).length > MAX_METADATA_BYTES ||
        row.checksum !== hashBytes(encoder.encode(`${key}:${row.json}`))
      ) {
        records.metadata.delete(key);
      } else
        entries.push({
          key,
          kind: "metadata",
          bytes: encoder.encode(row.json).length + encoder.encode(key).length + 128,
          age: row.lastUsed,
        });
    }
    let bytes = entries.reduce((total, entry) => total + entry.bytes, 0);
    for (const entry of entries.sort((a, b) => a.age - b.age)) {
      if (bytes <= this.maxBytes) break;
      records[entry.kind].delete(entry.key);
      bytes -= entry.bytes;
    }
    for (const [key, row] of records.pages)
      if (!records.bundles.has(row.id)) records.pages.delete(key);
  }

  /** Read only the admitted bundle, never enumerate binary payloads during accounting. */
  private async binaryBlob(bundle: BundleRecord): Promise<Blob | undefined> {
    const db = await this.open();
    if (!db || this.closed) return undefined;
    this.binaryStorage = true;
    return new Promise(resolve => {
      try {
        const tx = db.transaction(PAYLOADS);
        let blob: Blob | undefined;
        tx.oncomplete = () => resolve(blob);
        tx.onabort = () => resolve(undefined);
        const request = tx.objectStore(PAYLOADS).get(bundle.id);
        request.onsuccess = () => {
          try {
            const bytes: unknown = request.result?.bytes;
            if (bytes instanceof ArrayBuffer && bytes.byteLength === bundle.byteLength)
              blob = new Blob([bytes], { type: "application/pdf" });
          } catch {
            /* Allocation or structured-clone failure is a cache miss. */
          }
        };
      } catch {
        resolve(undefined);
      }
    });
  }

  /** Return validated hits by page-content key, hashing each shared PDF only once per lookup. */
  async lookup(keys: readonly string[]): Promise<Map<string, CachedArtifactPage>> {
    const hits = new Map<string, CachedArtifactPage>();
    const generation = this.generation;
    try {
      const candidates = await this.update((records, tick) => {
        const found: { key: string; page: PageRecord; bundle: BundleRecord }[] = [];
        for (const key of new Set(keys)) {
          const page = records.pages.get(key);
          const bundle = page && records.bundles.get(page.id);
          if (page && bundle) {
            found.push({ key, page, bundle });
            records.bundles.set(bundle.id, { ...bundle, lastUsed: tick });
          }
        }
        return found;
      });
      const checked = new Map<string, Blob | undefined>();
      for (const { key, page, bundle } of candidates ?? []) {
        if (!checked.has(bundle.id)) {
          let valid: Blob | undefined;
          try {
            const blob = bundle.blob ?? (await this.binaryBlob(bundle));
            if (blob && (await checksum(blob, bundle.pageCount)) === bundle.checksum) valid = blob;
          } catch {
            /* Cache miss. */
          }
          checked.set(bundle.id, valid);
        }
        if (checked.get(bundle.id))
          hits.set(key, {
            id: bundle.id,
            blob: checked.get(bundle.id)!,
            pageCount: bundle.pageCount,
            sourcePage: page.sourcePage,
          });
      }
      if ([...checked.values()].includes(undefined))
        await this.update(records => {
          for (const [id, valid] of checked) if (!valid) records.bundles.delete(id);
        });
    } catch {
      /* Storage is never authoritative. */
    }
    return generation === this.generation && !this.closed ? hits : new Map();
  }

  /** Atomically publish a PDF and all its content-key mappings, or retain the old entries. */
  async put(bundle: ArtifactBundle): Promise<void> {
    const generation = this.generation;
    try {
      const { blob, pageCount } = bundle;
      const pages = bundle.pages.map(page => ({ ...page }));
      if (
        !(blob instanceof Blob) ||
        blob.size > this.maxBytes ||
        !positive(pageCount) ||
        pageCount > 4096 ||
        !pages.length ||
        pages.length > 4096 ||
        new Set(pages.map(page => page.key)).size !== pages.length ||
        pages.some(
          page =>
            !validKey(page.key) ||
            !positive(page.logicalPage) ||
            !positive(page.sourcePage) ||
            page.sourcePage > pageCount,
        )
      )
        return;
      // Reject an individually oversized publication before replacing any existing keys.
      const bytes =
        blob.size +
        128 +
        pages.reduce((sum, page) => sum + encoder.encode(page.key).length + 128, 0);
      if (bytes > this.maxBytes) return;
      const digest = await checksum(blob, pageCount);
      if (generation !== this.generation || this.closed) return;
      const id =
        globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}-${Math.random()}`;
      const publish = (stored: Blob | ArrayBuffer) =>
        this.update((records, tick, tx) => {
          if (generation !== this.generation || this.closed) return;
          if (stored instanceof Blob && [...records.bundles.values()].some(row => !row.blob))
            return false;
          if (stored instanceof ArrayBuffer && bytes + 128 > this.maxBytes) return false;
          records.bundles.set(id, {
            id,
            ...(stored instanceof Blob ? { blob: stored } : { byteLength: stored.byteLength }),
            pageCount,
            checksum: digest,
            lastUsed: tick,
          });
          if (stored instanceof ArrayBuffer) tx.objectStore(PAYLOADS).put({ id, bytes: stored });
          for (const page of pages)
            records.pages.set(page.key, { ...page, id, checksum: pageChecksum(page, id) });
          return true;
        });
      // WebKit may allow IDB but reject Blob serialization (notably ephemeral contexts).
      // Retry the atomic publication with bytes; never await Blob reads inside a transaction.
      if (
        (this.binaryStorage || !(await publish(blob))) &&
        blob.size <= MAX_BINARY_BYTES &&
        generation === this.generation &&
        !this.closed
      ) {
        this.binaryStorage = true;
        await publish(await blob.arrayBuffer());
      }
    } catch {
      /* Quota, serialization and storage denial are optimization failures only. */
    }
  }

  /** Retrieve JSON metadata; the caller validates its schema and source identity. */
  async getMetadata(key: string): Promise<unknown | undefined> {
    try {
      return await this.update((records, tick) => {
        const row = records.metadata.get(key);
        if (!row) return undefined;
        try {
          const value: unknown = JSON.parse(row.json);
          records.metadata.set(key, { ...row, lastUsed: tick });
          return value;
        } catch {
          records.metadata.delete(key);
          return undefined;
        }
      });
    } catch {
      return undefined;
    }
  }

  /** Store caller-validated JSON metadata, bounded by both the record and total budgets. */
  async putMetadata(key: string, value: unknown): Promise<void> {
    try {
      const json = JSON.stringify(value);
      if (!validKey(key) || typeof json !== "string") return;
      const bytes = encoder.encode(json).length;
      if (bytes > MAX_METADATA_BYTES || bytes + encoder.encode(key).length + 128 > this.maxBytes)
        return;
      await this.update((records, tick) => {
        records.metadata.set(key, {
          key,
          json,
          lastUsed: tick,
          checksum: hashBytes(encoder.encode(`${key}:${json}`)),
        });
      });
    } catch {
      /* Non-JSON metadata and denied storage are not authoritative errors. */
    }
  }

  /** Permanently close this instance, without invalidating any returned Blob. */
  close(): void {
    this.closed = true;
    this.generation++;
    this.database?.close();
    this.database = undefined;
  }

  /** Clear only derivatives and metadata, never CacheStorage source resources. */
  async clear(): Promise<void> {
    this.generation++;
    await this.update((records, _tick, tx) => {
      records.bundles.clear();
      records.pages.clear();
      records.metadata.clear();
      tx.objectStore(PAYLOADS).clear();
    });
  }
}

/** Clear the application's default derivative database without deleting source caches. */
export async function clearMultikeyArtifacts(indexedDB?: IDBFactory): Promise<void> {
  const cache = new MultikeyArtifactCache({ indexedDB });
  try {
    await cache.clear();
  } finally {
    cache.close();
  }
}
